import { Helmet } from 'react-helmet';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryData, usePostMessage, useDlogEvent } from 'spectacle-ui-game-bridge-react';
import { getPath } from '../../config';
import Build from '../../components/build/build';
import Debug from '../../components/debug/debug';
import ErrorModal from '../../components/errorModal/errorModal';
import ExternalLinkIcon from '../../icons/ExternalLinkIcon';
import LoadingIndicator from '../../components/loadingIndicator/loadingIndicator';
import useBodyBackground from '../../hooks/useBodyBackground';
import useCDNI from '../../hooks/useCDNI';
import useContentForBuild from '../../hooks/useContentForBuild';
import useDlogLoginFunnel from '../../hooks/useDlogLoginFunnel';
import './acceptance.sass';
import CameraIcon from '../../icons/CameraIcon';
import MicrophoneIcon from '../../icons/MicrophoneIcon';

export default function AcceptanceKO() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState(1);
  const [systemCountry] = useQueryData<string>('game', 'systemCountry');
  const [acceptedPrivacyPolicyVersion] = useQueryData('game', 'privacypolicyversion');
  const [acceptedTosVersion] = useQueryData('game', 'tosversion');
  const [acceptedTouVersion] = useQueryData('game', 'touversion');
  const [acceptedPIIVersion] = useQueryData('game', 'piiversion');
  const [isCDNIFinished] = useQueryData<boolean>('tos', 'isCDNIFinished');
  const openUrl = usePostMessage('openUrl');
  const dlogAcceptanceShown = useDlogEvent('dlog_event_acceptance_shown');
  const dlog = useDlogLoginFunnel();
  const acceptToS = usePostMessage('acceptToS');
  const acceptToU = usePostMessage('acceptToU');
  const acceptPrivacyPolicy = usePostMessage('acceptPrivacyPolicy');
  const acceptPII = usePostMessage('acceptPII');
  const passPreloginFence = usePostMessage('passPreloginFence');
  const [content, loading, errorContent] = useContentForBuild();
  const isKoreanRegion = systemCountry === 'KR';
  useBodyBackground(`url(${getPath('bg_general')}) no-repeat top center fixed #000`);

  const { error } = useCDNI(content);

  const [acceptance, setAcceptance] = useState({
    serviceAcceptance: false,
    termsOfUseAcceptance: false,
    privacyPolicyAcceptance: false,
    acceptedPIIVersion: false,
  });

  const isButtonDisabled = !acceptance.serviceAcceptance || !acceptance.acceptedPIIVersion || !acceptance.privacyPolicyAcceptance || !acceptance.termsOfUseAcceptance;

  const handleChange = (e: any) => {
    const value = e.target.checked;
    const name = e.target.name;

    setAcceptance((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUrl = useCallback(
    (e: any) => {
      e.preventDefault();
      const href = e.target.href;

      openUrl(href);
    },
    [openUrl]
  );

  const handleClickTermsOfUse = (event: any) => {
    dlog({
      phase: 'Legal_URLs',
      action: 'Click',
      targets: 'Terms of Use',
    });
    handleUrl(event);
  };

  const handleClickTermsOfService = (event: any) => {
    dlog({
      phase: 'Legal_URLs',
      action: 'Click',
      targets: 'Terms of Service',
    });
    handleUrl(event);
  };

  const handleClickPIICollection = (event: any) => {
    dlog({
      phase: 'Legal_URLs',
      action: 'Click',
      targets: 'PII Collection',
    });
    handleUrl(event);
  };

  const handleClickPrivacyPolicy = (event: any) => {
    dlog({
      phase: 'Legal_URLs',
      action: 'Click',
      targets: 'Privacy Policy',
    });
    handleUrl(event);
  };

  const accept = (e: any) => {
    e.preventDefault();

    if (phase === 1) {
      setPhase(2);
      acceptToS(content?.serviceAgreementVersion);
      acceptToU(content?.termsOfUseAgreementVersion);
      acceptPrivacyPolicy(content?.privacyPolicyAgreementVersion);
      acceptPII(content?.koTermsPIICollectionVersion);

      dlog({
        phase: 'EULA',
        action: 'Click',
        targets: 'Agree',
      });

      return;
    }

    if (content?.webBasedLogin) {
      (window as any).hasPassedAcceptance = true;

      passPreloginFence();

      if (!isCDNIFinished) {
        navigate('/cdni');
      } else {
        navigate('/login');
      }
    } else {
      passPreloginFence();
    }
  };

  // Set Initial Acceptance
  useEffect(() => {
    if (content) {
      setAcceptance({
        serviceAcceptance: content.serviceAgreementVersion === acceptedTosVersion,
        termsOfUseAcceptance: content.termsOfUseAgreementVersion === acceptedTouVersion,
        privacyPolicyAcceptance: content.privacyPolicyAgreementVersion === acceptedPrivacyPolicyVersion,
        acceptedPIIVersion: content.koTermsPIICollectionVersion === acceptedPIIVersion,
      });
    }
  }, [acceptedPIIVersion, acceptedPrivacyPolicyVersion, acceptedTosVersion, acceptedTouVersion, content]);

  useEffect(() => {
    dlogAcceptanceShown({});
  }, [dlogAcceptanceShown]);

  useEffect(() => {
    dlog({
      phase: 'EULA',
      action: 'Render',
    });
  }, [dlog]);

  return (
    <>
      <Helmet>
        <title>{content?.reviewTerms}</title>
      </Helmet>
      {loading && (
        <div className="loading-mask">
          <LoadingIndicator />
        </div>
      )}
      {content && (
        <div className="centered">
          <div className="acceptance">
            <div className="logo-inline">
              <img src={getPath('menu_cod_logo_lg')} alt="Call of Duty" />
              <h1>{phase === 1 ? content.koTermsTitle1 : content.koTermsTitle2}</h1>
            </div>
            {phase === 1 && (
              <>
                <div className="checkbox">
                  <input type="checkbox" id="serviceAcceptance" name="serviceAcceptance" checked={acceptance.serviceAcceptance} onChange={handleChange} />
                  <div>
                    <label htmlFor="serviceAcceptance">
                      <div>{content.serviceAcceptance}</div>
                      <a onClick={handleClickTermsOfService} href={content.serviceAgreementUrl}>
                        {content.serviceCta} <ExternalLinkIcon className="external-link__icon" />
                      </a>
                    </label>
                  </div>
                </div>
                <div className="checkbox">
                  <input type="checkbox" id="termsOfUseAcceptance" name="termsOfUseAcceptance" checked={acceptance.termsOfUseAcceptance} onChange={handleChange} />
                  <div>
                    <label htmlFor="termsOfUseAcceptance">
                      <div>{content.termsOfUseAcceptance}</div>
                      <a onClick={handleClickTermsOfUse} href={content.termsOfUseAgreementUrl}>
                        {content.termsOfUseCta} <ExternalLinkIcon className="external-link__icon" />
                      </a>
                    </label>
                  </div>
                </div>
                <div className="checkbox">
                  <input type="checkbox" id="privacyPolicyAcceptance" name="privacyPolicyAcceptance" checked={acceptance.privacyPolicyAcceptance} onChange={handleChange} />
                  <div>
                    <label htmlFor="privacyPolicyAcceptance">
                      <div>{content.privacyPolicyAcceptance}</div>
                      <a onClick={handleClickPrivacyPolicy} href={content.privacyPolicyAgreementUrl}>
                        {content.privacyPolicyCta} <ExternalLinkIcon className="external-link__icon" />
                      </a>
                    </label>
                  </div>
                </div>
                <div className="checkbox">
                  <input type="checkbox" id="acceptedPIIVersion" name="acceptedPIIVersion" checked={acceptance.acceptedPIIVersion} onChange={handleChange} />
                  <div>
                    <label htmlFor="acceptedPIIVersion">
                      <div>{content.koTermsPIICollectionAcceptance}</div>
                      <a onClick={handleClickPIICollection} href={content.koTermsPIICollectionUrl}>
                        {content.koTermsPIICollectionCta} <ExternalLinkIcon className="external-link__icon" />
                      </a>
                    </label>
                  </div>
                </div>
              </>
            )}
            {phase === 2 && (
              <>
                <p>{content.koTermsDesc2}</p>
                <div className="acceptance__permissions">
                  <div className="acceptance__permission">
                    <div className="acceptance__permission-icon">
                      <MicrophoneIcon />
                    </div>
                    <div>
                      <h3 className="acceptance__permission-heading">
                        {content.koTermsMicrophonePermission} <span>{content.koTermsOptional}</span>
                      </h3>
                      <p className="acceptance__permission-desc">{content.koTermsMicrophonePermissionDesc}</p>
                    </div>
                  </div>

                  <div className="acceptance__permission">
                    <div className="acceptance__permission-icon">
                      <CameraIcon />
                    </div>
                    <div>
                      <h3 className="acceptance__permission-heading">
                        {content.koTermsCameraPermission} <span>{content.koTermsOptional}</span>
                      </h3>
                      <p className="acceptance__permission-desc">{content.koTermsCameraPermissionDesc}</p>
                    </div>
                  </div>
                </div>
                <p className="acceptance__optional-desc">{content.koTermsOptionalDesc}</p>
              </>
            )}
            <button disabled={isButtonDisabled} className="cta cta--wide" onClick={accept}>
              {content.continue}
            </button>
          </div>
        </div>
      )}
      <div className="acceptance__rating" />
      <div className="build">
        <Build />
      </div>
      <ErrorModal error={error} />
      <Debug
        enabled={!!content?.showDeveloperOverlay}
        vars={{
          errorContent,
          systemCountry,
          isKoreanRegion,
        }}
      >
        <button onClick={() => window.location.reload()}>reload</button>
      </Debug>
    </>
  );
}
